import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';

import amplitude from '../../lib/amplitude';
import { track } from '../../lib/analytics';
import { authService, User as AuthUser } from '../services/authService';
import { supabase } from '../services/supabaseService';

// Use the User type from authService
type User = AuthUser;

type AnalyticsProperties = Record<string, unknown>;

interface OnboardingProgress {
  currentStep: string;
  completedSteps: string[];
  skippedSteps: string[];
  startedAt: string;
  completedAt?: string;
  userChoices: {
    joinedGroup?: boolean;
    completedTutorial?: boolean;
    sharedReferral?: boolean;
  };
}

interface ReferralData {
  referral_code: string;
  referral_link: string;
  total_referrals: number;
  successful_referrals: number;
  rewards_earned: number;
}

interface AppState {
  // User data
  user: User | null;
  isAuthenticated: boolean;
  
  // Onboarding
  onboardingProgress: OnboardingProgress | null;
  
  // Referral system
  referralData: ReferralData | null;
  
  // Admin permissions
  adminPermissions: {
    canManageContent: boolean;
    canManageUsers: boolean;
    canViewAnalytics: boolean;
  };
  
  // App state
  loading: boolean;
  error: string | null;
}

type AppAction = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_ONBOARDING_PROGRESS'; payload: OnboardingProgress }
  | { type: 'SET_REFERRAL_DATA'; payload: ReferralData }
  | { type: 'UPDATE_USER_CHOICES'; payload: Partial<OnboardingProgress['userChoices']> }
  | { type: 'RESET_STATE' };

// Initial state
const initialState: AppState = {
  user: null,
  isAuthenticated: false,
  onboardingProgress: null,
  referralData: null,
  adminPermissions: {
    canManageContent: false,
    canManageUsers: false,
    canViewAnalytics: false
  },
  loading: false,
  error: null
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    
    case 'SET_USER':
      return { 
        ...state, 
        user: action.payload,
        isAuthenticated: !!action.payload,
        adminPermissions: action.payload ? {
          canManageContent: action.payload.is_admin,
          canManageUsers: action.payload.is_admin,
          canViewAnalytics: action.payload.is_admin
        } : initialState.adminPermissions
      };
    
    case 'SET_ONBOARDING_PROGRESS':
      return { ...state, onboardingProgress: action.payload };
    
    case 'SET_REFERRAL_DATA':
      return { ...state, referralData: action.payload };
    
    case 'UPDATE_USER_CHOICES':
      return {
        ...state,
        onboardingProgress: state.onboardingProgress ? {
          ...state.onboardingProgress,
          userChoices: {
            ...state.onboardingProgress.userChoices,
            ...action.payload
          }
        } : null
      };
    
    case 'RESET_STATE':
      return initialState;
    
    default:
      return state;
  }
}

// Context
interface AppContextType {
  state: AppState;
  
  // User actions
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateUserProfile: (updates: Partial<User>) => Promise<void>;
  
  // Onboarding actions
  updateOnboardingProgress: (progress: OnboardingProgress) => Promise<void>;
  completeOnboarding: () => Promise<void>;
  
  // Referral actions
  loadReferralData: () => Promise<void>;
  trackReferral: (referralCode: string) => Promise<void>;
  
  // Analytics actions
  trackEvent: (eventName: string, properties?: AnalyticsProperties) => void;
  trackOnboardingStep: (step: string, properties?: AnalyticsProperties) => void;
  
  // Utility actions
  clearError: () => void;
  refreshUserData: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Initialize app data
  useEffect(() => {
    initializeApp();
  }, []);

  const initializeApp = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      // Initialize auth service and check existing session
      const user = await authService.initialize();
      
      if (user) {
        dispatch({ type: 'SET_USER', payload: user });
        await loadReferralData();
        await loadOnboardingProgress();
      }
    } catch (error: unknown) {
      console.error('Failed to initialize app:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to initialize app' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const loadUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;

      dispatch({ type: 'SET_USER', payload: data });
      
      // Set user ID for analytics
      amplitude.setUserId(userId);
    } catch (error: unknown) {
      console.error('Failed to load user profile:', error);
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const user = await authService.signIn({ email, password });
      dispatch({ type: 'SET_USER', payload: user });
      
      await loadReferralData();
      await loadOnboardingProgress();
      
      trackEvent('user_signed_in', { method: 'email' });
    } catch (error: unknown) {
      dispatch({ type: 'SET_ERROR', payload: getErrorMessage(error, 'Failed to sign in') });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const result = await authService.signUp({ email, password, name });
      // SignUp returns a confirmation status, not a user object
      // User will be set after email confirmation
      console.log('Sign up result:', result);
      
      await loadReferralData();
      
      trackEvent('user_signed_up', { method: 'email' });
    } catch (error: unknown) {
      dispatch({ type: 'SET_ERROR', payload: getErrorMessage(error, 'Failed to sign up') });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const signOut = async () => {
    try {
      await authService.signOut();
      dispatch({ type: 'RESET_STATE' });
      
      trackEvent('user_signed_out');
    } catch (error: unknown) {
      dispatch({ type: 'SET_ERROR', payload: getErrorMessage(error, 'Failed to sign out') });
      throw error;
    }
  };

  const updateUserProfile = async (updates: Partial<User>) => {
    if (!state.user) return;

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', state.user.id);

      if (error) throw error;

      dispatch({ type: 'SET_USER', payload: { ...state.user, ...updates } });
      
      trackEvent('user_profile_updated', { fields: Object.keys(updates) });
    } catch (error: unknown) {
      dispatch({ type: 'SET_ERROR', payload: getErrorMessage(error, 'Failed to update profile') });
      throw error;
    }
  };

  const updateOnboardingProgress = async (progress: OnboardingProgress) => {
    dispatch({ type: 'SET_ONBOARDING_PROGRESS', payload: progress });
    
    try {
      // Save to AsyncStorage for persistence
      await AsyncStorage.setItem('@onboarding_progress', JSON.stringify(progress));
      
      trackOnboardingStep(progress.currentStep, {
        completed_steps: progress.completedSteps.length,
        skipped_steps: progress.skippedSteps.length,
        user_choices: progress.userChoices
      });
    } catch (error: unknown) {
      console.error('Failed to save onboarding progress:', error);
    }
  };

  const completeOnboarding = async () => {
    if (!state.user) return;

    try {
      await updateUserProfile({ onboarding_completed: true });
      await AsyncStorage.removeItem('@onboarding_progress');
      
      trackEvent('onboarding_completed', {
        duration: state.onboardingProgress ? 
          new Date().getTime() - new Date(state.onboardingProgress.startedAt).getTime() : 0,
        user_choices: state.onboardingProgress?.userChoices
      });
    } catch (error: unknown) {
      dispatch({ type: 'SET_ERROR', payload: getErrorMessage(error, 'Failed to complete onboarding') });
      throw error;
    }
  };

  const loadReferralData = async () => {
    if (!state.user) return;

    try {
      // TODO: Replace with actual API call
      const mockReferralData: ReferralData = {
        referral_code: `FAITH${state.user.id.slice(-4).toUpperCase()}`,
        referral_link: `https://kingdomquiz.app/join/FAITH${state.user.id.slice(-4).toUpperCase()}`,
        total_referrals: 0,
        successful_referrals: 0,
        rewards_earned: 0
      };

      dispatch({ type: 'SET_REFERRAL_DATA', payload: mockReferralData });
    } catch (error: unknown) {
      console.error('Failed to load referral data:', error);
    }
  };

  const loadOnboardingProgress = async () => {
    if (!state.user) return;

    try {
      // Load from AsyncStorage first
      const savedProgress = await AsyncStorage.getItem('@onboarding_progress');
      if (savedProgress) {
        const progress: OnboardingProgress = JSON.parse(savedProgress);
        dispatch({ type: 'SET_ONBOARDING_PROGRESS', payload: progress });
        return;
      }

      // Load from database
      const { data, error } = await supabase
        .from('onboarding_progress')
        .select('*')
        .eq('user_id', state.user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      if (data) {
        const progress: OnboardingProgress = {
          currentStep: data.current_step,
          completedSteps: data.completed_steps,
          skippedSteps: data.skipped_steps,
          startedAt: data.started_at,
          completedAt: data.completed_at,
          userChoices: data.user_choices
        };
        dispatch({ type: 'SET_ONBOARDING_PROGRESS', payload: progress });
      }
    } catch (error: unknown) {
      console.error('Failed to load onboarding progress:', error);
    }
  };

  const trackReferral = async (referralCode: string) => {
    try {
      // TODO: Implement referral tracking
      trackEvent('referral_used', { referral_code: referralCode });
    } catch (error: unknown) {
      console.error('Failed to track referral:', error);
    }
  };

  const trackEvent = (eventName: string, properties: AnalyticsProperties = {}) => {
    try {
      track(eventName, {
        ...properties,
        user_id: state.user?.id,
        timestamp: new Date().toISOString()
      });
    } catch (error: unknown) {
      console.error('Failed to track event:', error);
    }
  };

  const trackOnboardingStep = (step: string, properties: AnalyticsProperties = {}) => {
    trackEvent('onboarding_step_completed', {
      step,
      ...properties
    });
  };

  const clearError = () => {
    dispatch({ type: 'SET_ERROR', payload: null });
  };

  const refreshUserData = async () => {
    if (!state.user) return;

    try {
      await loadUserProfile(state.user.id);
      await loadReferralData();
    } catch (error: unknown) {
      dispatch({ type: 'SET_ERROR', payload: getErrorMessage(error, 'Failed to refresh user data') });
      throw error;
    }
  };

  const contextValue: AppContextType = {
    state,
    signIn,
    signUp,
    signOut,
    updateUserProfile,
    updateOnboardingProgress,
    completeOnboarding,
    loadReferralData,
    trackReferral,
    trackEvent,
    trackOnboardingStep,
    clearError,
    refreshUserData
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
